'use client';

/**
 * api-client — barrel re-exportador (shim Estrategia A).
 *
 * SPEC-244 IA17 — Split de frontend/src/lib/api-client.ts (9.614 L) en
 * módulos ≤1.000 L bajo api-client/{core,namespaces}/.
 *
 * API pública inmutable: este barrel preserva los exports del archivo
 * original. Los consumers no cambian sus imports:
 *   import { api, apiFetch, tokenStore, ApiClientError } from '@/lib/api-client';
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 */

// Core: fetch wrapper + tokenStore + ApiClientError + tipos/interfaces
export { apiFetch, apiFetchValidated, apiFetchFormData, tryRefresh, tryRefreshDetailed } from './core/fetch-client';
export { tokenStore } from './core/token-store';
export { ApiClientError, SERVER_ERROR_MESSAGES } from './core/error-handler';

export type { ValidatableSchema } from './core/fetch-client';

export type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from './core/types';

export type {
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  ConferenceRoomResponse, InterviewRoomResponse,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from './core/interfaces';

// Re-export ALL types from shared-types (forwarded from @profileia/shared)
export type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  DepositStatus as UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, AdminDepositStatus, AdminDepositStatus as DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
  // V139-RECURRENT-ROOMS-FRONTEND: recurrence types (mirror of the backend
  // IRoomRecurrence shape). Re-exported from `./core/shared-types` which
  // itself re-exports from `@profileia/shared` (defined in
  // packages/shared/src/types/room-recurrence.ts).
  RoomRecurrence,
  RoomRecurrenceCreateInput,
  RoomRecurrencePatchInput,
  RecurrencePattern,
  RecurrenceStatus,
} from './core/shared-types';

// Namespaces — importar como objetos, no spread, para preservar `api.<namespace>.<method>` surface
import { userMethods } from './namespaces/user';
import { kyc } from './namespaces/kyc';
import { admin } from './namespaces/admin';
import { interviews } from './namespaces/interviews';
import { matrix } from './namespaces/matrix';
import { qualification } from './namespaces/qualification';
import { deposit } from './namespaces/deposit';
import { payout } from './namespaces/payout';
import { packageMethods } from './namespaces/packages';
import { landing } from './namespaces/landing';
import { systemLanding } from './namespaces/system-landing';
import { academy } from './namespaces/academy';
import { credits } from './namespaces/credits';
import { seller } from './namespaces/seller';
import { market } from './namespaces/market';
import { reservations } from './namespaces/reservations';
import { directMessages } from './namespaces/direct-messages';
import { push } from './namespaces/notifications';
import { adminGeo } from './namespaces/geo-admin';
import { geo } from './namespaces/geo';
import { welcomeMessage } from './namespaces/announcements';
import { forbiddenWords } from './namespaces/forbidden-words';
import { userAddresses } from './namespaces/contact';
import { booking } from './namespaces/booking';
// SPEC-AI-CONTEXT-FILE-STORAGE PR2 (FE storage page) — `userFiles` namespace
// wraps the 8 user-facing endpoints of `/api/user-files/*` mounted by PR1.
// The 2 admin-only triggers (billing/run + cleanup/run) are excluded until
// PR3 wires the actual jobs (PR1 returns 501 USER_FILE_NOT_IMPLEMENTED).
// Spec ref: docs/SPEC-AI-CONTEXT-FILE-STORAGE.md §5.1, §7.1, §11 (PR2).
import { userFiles } from './namespaces/user-files';
// V126-F2-FE-WALLET-AUTO-WITHDRAW-SECTION — `autoWithdraw` namespace wraps
// the 3 user-facing endpoints mounted at /api/wallet/auto-withdraw-* by
// V126-F2 (settings GET/PATCH + history GET). Anti-P-382: thin wrapper —
// no business logic, no parallel mechanism on the FE.
import { autoWithdraw } from './namespaces/auto-withdraw';
// CONECTA-PR3-FE-HUB-OPT-IN — `conecta` namespace wraps the 13 endpoints
// under /api/conecta/* mounted by PR2 (6 public + 7 auth; 2 are 501 stubs
// handled gracefully — boost PR6, createReview PR5). Two named exports
// (`conectaPublic` + `conectaAuth`) instead of a single object — the PR3
// Hub settings page calls auth endpoints while the future PR4 /conecta
// public page calls public endpoints; the separation matches the BE
// router split (`conecta-public.controller.ts` + `conecta-auth.controller.ts`).
// Anti-P-382: thin wrapper — no business logic, no parallel mechanism on FE.
import { conectaPublic, conectaAuth } from './namespaces/conecta';
// CONECTA-PR7-MODERATION-REPORTS — `conectaAdmin` namespace wraps the 5
// PR7 admin endpoints (categories pending/approve/reject + reports list/
// resolve + listings hide/restore). Mirrors the BE controller routes
// mounted under `/api/conecta/admin/*` (all gated by `requireAuth +
// requireAdmin`). Anti-P-383: extracted to its own file (`conecta-admin.ts`)
// because `conecta.ts` was 495L + 200L admin namespace = 696L > 500L target
// (mirrors the SPEC-244 IA17 split precedent — `admin-market-reviews.ts`
// extracted from `admin-market.ts`). Anti-P-382: thin wrapper — no business
// logic, no parallel mechanism on FE.
import { conectaAdmin } from './namespaces/conecta-admin';
// V139-RECURRENT-ROOMS-FRONTEND (Founder directive 2026-11-02) — `conferences`
// namespace wraps the 4 recurrence endpoints (POST/PATCH/DELETE/GET
// /api/conferences/:roomId/recurrence) mounted by PR #1597
// (V138-RECURRENT-ROOMS-BACKEND). The legacy conference methods
// (`getConferences`, `createConference`, `updateRoomSettings`,
// `uploadRoomBranding`, `getConference`) remain on the top-level `api.*`
// surface (in `./namespaces/user.ts`) for backward compat — moving them
// would break existing imports across the app. This namespace adds ONLY
// the recurrence methods, exposed as `api.conferences.createRecurrence(...)`,
// etc. Anti-P-383: extracted to its own file (≤500L target). Anti-P-382:
// thin wrapper — no business logic, no parallel mechanism on FE.
import { conferences } from './namespaces/conferences';

// V126-F3-ADMIN-AUTO-WITHDRAW-DASHBOARD — type re-exports for the admin
// monitoring dashboard. Defined in `./namespaces/admin` (FE mirror of the
// backend DTOs in `backend/src/features/admin/auto-withdraw.ts`). Anti-§6
// (CM-19/CM-15): NO duplication of the `PayoutDto` / `Paginated<T>` shapes
// already re-exported above — these 3 types are the NEW shapes for the
// stats + users endpoints (history reuses `Paginated<PayoutDto>`).
export type {
  AutoWithdrawStats,
  AutoWithdrawUserDto,
  ListAutoWithdrawHistoryQuery,
} from './namespaces/admin';

// V126-F2-FE-WALLET-AUTO-WITHDRAW-SECTION — FE mirror of the backend
// DTOs in `backend/src/controllers/auto-withdraw.controller.ts`. Re-exported
// here so callers can `import type { AutoWithdrawSettings, ... } from
// '@/lib/api-client'` (same pattern as the existing type re-exports).
export type {
  AutoWithdrawSettings,
  UpdateAutoWithdrawSettingsBody,
  AutoWithdrawHistoryItem,
  AutoWithdrawHistoryResponse,
} from './namespaces/auto-withdraw';

// CONECTA-PR3-FE-HUB-OPT-IN — FE mirror of the backend DTOs in
// `backend/src/features/conecta/conecta-public.controller.ts` +
// `backend/src/features/conecta/conecta-auth.controller.ts`. Re-exported
// here so callers can `import type { ConectaListingDetail, ... } from
// '@/lib/api-client'` (same pattern as the existing type re-exports).
// CONECTA-PR5 — added review lifecycle types (ConectaReviewView +
// CreateReviewRequest + UpdateReviewRequest + DeleteReviewResponse).
// CONECTA-PR7 — added admin namespace types (ConectaAdminPendingCategory +
// ConectaPendingCategoriesResponse + ConectaAdminCategory + ConectaAdminReport
// + ConectaAdminReportsResponse + ResolveReportBody + AdminHideListingBody +
// AdminRejectCategoryBody + ConectaAdminListingView).
// TASK-FEAT-CONECTA-ADMIN-CREATE-CATEGORIES (2026-10-26) — added
// AdminCreateCategoryBody + AdminDeleteCategoryResponse for the direct
// create + delete category endpoints.
export type {
  ConectaCategory,
  ConectaListingCard,
  ConectaListingsResponse,
  ConectaListingDetail,
  ConectaLocation,
  ConectaReview,
  ConectaReviewsResponse,
  OptInRequest,
  UpdateListingRequest,
  SuggestCategoryRequest,
  SuggestCategoryResponse,
  OptOutResponse,
  NotImplementedStub,
  ConectaReviewView,
  CreateReviewRequest,
  UpdateReviewRequest,
  DeleteReviewResponse,
  ConectaAdminPendingCategory,
  ConectaPendingCategoriesResponse,
  ConectaAdminCategory,
  ConectaAdminReport,
  ConectaAdminReportsResponse,
  ResolveReportBody as ConectaResolveReportBody,
  AdminHideListingBody as ConectaAdminHideListingBody,
  AdminRejectCategoryBody as ConectaAdminRejectCategoryBody,
  ConectaAdminListingView,
  AdminCreateCategoryBody as ConectaAdminCreateCategoryBody,
  AdminDeleteCategoryResponse as ConectaAdminDeleteCategoryResponse,
} from './namespaces/conecta';

/**
 * `api` — único objeto exportado. Preserva la surface del archivo original.
 * El orden sigue el archivo original (L2248-L9603) para preservar key-order.
 */
export const api = {
  ...userMethods,
  kyc,
  admin,
  interviews,
  matrix,
  qualification,
  deposit,
  payout,
  ...packageMethods,
  landing,
  systemLanding,
  academy,
  credits,
  seller,
  market,
  reservations,
  directMessages,
  push,
  adminGeo,
  geo,
  welcomeMessage,
  forbiddenWords,
  userAddresses,
  booking,
  userFiles,
  autoWithdraw,
  conectaPublic,
  conectaAuth,
  conectaAdmin,
  conferences,
};

// CONECTA-PR3 R2 recovery fix: also export conectaPublic + conectaAuth as
// named exports so components can `import { conectaAuth } from '@/lib/api-
// client'` (matches the canonical `auto-withdraw-section.tsx` pattern
// which uses `api.autoWithdraw.method()` but the conecta PR3 IA Programadora
// chose named imports — both patterns now supported).
export { conectaPublic, conectaAuth };
// CONECTA-PR7-MODERATION-REPORTS — also export conectaAdmin as a named
// export so the admin dashboard can `import { conectaAdmin } from
// '@/lib/api-client'` (mirrors the conectaPublic + conectaAuth named
// exports pattern). Anti-P-347: same export style as the existing
// conectaPublic/conectaAuth named exports above.
export { conectaAdmin };

// V139-RECURRENT-ROOMS-FRONTEND — also export `conferences` as a named
// export so callers can `import { conferences } from '@/lib/api-client'`
// (mirrors the `conectaAdmin` / `conectaAuth` named-export pattern above).
// Anti-P-347: same export style as the existing conectaAdmin named export.
export { conferences };
